'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowLeft, Plus, Trash, Trophy } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useDocumentTitle } from '@/lib/use-document-title';
import { cn } from '@/lib/utils';
import {
  ARENA_MODALITIES,
  ARENA_GENERATOR_KINDS,
  ARENA_DEFAULT_BUDGET_CAP_CREDITS,
  createArenaExperimentSchema,
  type ArenaModality,
  type ArenaGeneratorKind,
} from '@/lib/arena/types';
import { MODALITY_META } from '@/components/arena/constants';

interface VariantRow {
  key: string;
  label: string;
  generatorKind: ArenaGeneratorKind;
  model: string;
  promptTemplate: string;
  configOverridesText: string;
}

const DEFAULT_KIND: Record<ArenaModality, ArenaGeneratorKind> = {
  image: 'fal',
  video: 'fal',
  avatar: 'modal-avatar',
  'html-viz': 'llm-html',
};

let seq = 0;
function newVariant(modality: ArenaModality): VariantRow {
  seq += 1;
  return {
    key: `v${seq}`,
    label: '',
    generatorKind: DEFAULT_KIND[modality],
    model: '',
    promptTemplate: '',
    configOverridesText: '',
  };
}

export function NewExperimentClient() {
  useDocumentTitle('New experiment · Arena');
  const router = useRouter();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [modality, setModality] = useState<ArenaModality>('image');
  const [prompt, setPrompt] = useState('');
  const [sourceImageUrl, setSourceImageUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [variants, setVariants] = useState<VariantRow[]>(() => [newVariant('image'), newVariant('image')]);
  const [sampleCount, setSampleCount] = useState(1);
  const [seedPolicy, setSeedPolicy] = useState<'fixed' | 'random'>('random');
  const [fixedSeed, setFixedSeed] = useState(42);
  const [budgetCap, setBudgetCap] = useState(ARENA_DEFAULT_BUDGET_CAP_CREDITS);
  const [submitting, setSubmitting] = useState(false);

  const isAvatar = modality === 'avatar';

  const setVariant = (key: string, patch: Partial<VariantRow>) =>
    setVariants((prev) => prev.map((v) => (v.key === key ? { ...v, ...patch } : v)));

  const changeModality = (m: ArenaModality) => {
    setModality(m);
    // Re-home each variant's generatorKind to the modality default unless the
    // user chose a non-default kind.
    setVariants((prev) => prev.map((v) => ({ ...v, generatorKind: DEFAULT_KIND[m] })));
  };

  const configErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    for (const v of variants) {
      const text = v.configOverridesText.trim();
      if (!text) continue;
      try {
        const parsed = JSON.parse(text);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          errs[v.key] = 'Must be a JSON object';
        }
      } catch {
        errs[v.key] = 'Invalid JSON';
      }
    }
    return errs;
  }, [variants]);

  const buildPayload = () => {
    const inputs: Record<string, unknown> = {};
    if (isAvatar) {
      if (sourceImageUrl.trim()) inputs.sourceImageUrl = sourceImageUrl.trim();
      if (audioUrl.trim()) inputs.audioUrl = audioUrl.trim();
    }
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      modality,
      taskSpec: {
        prompt: prompt.trim() || undefined,
        ...(Object.keys(inputs).length ? { inputs } : {}),
      },
      variants: variants.map((v) => ({
        label: v.label.trim() || undefined,
        generatorKind: v.generatorKind,
        model: v.model.trim() || undefined,
        promptTemplate: v.promptTemplate.trim() || undefined,
        configOverrides:
          v.configOverridesText.trim() ? (JSON.parse(v.configOverridesText) as Record<string, unknown>) : undefined,
      })),
      seedPolicy,
      fixedSeed: seedPolicy === 'fixed' ? fixedSeed : undefined,
      sampleCountPerVariant: sampleCount,
      budgetCapCredits: budgetCap,
    };
  };

  const submit = async () => {
    if (Object.keys(configErrors).length) {
      toast.error('Fix the config JSON errors first');
      return;
    }
    const payload = buildPayload();
    const parsed = createArenaExperimentSchema.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      toast.error(first ? `${first.path.join('.')}: ${first.message}` : 'Invalid experiment');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/arena/experiments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Create failed (${res.status})`);
      toast.success('Experiment created');
      router.push(`/arena/${data.experiment.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Create failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      <Toaster position="top-center" richColors closeButton />
      <div className="relative mx-auto flex min-h-[calc(100vh-1.5rem)] max-w-3xl flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <button
            type="button"
            onClick={() => router.push('/arena')}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Back"
          >
            <ArrowLeft weight="bold" className="size-4" />
          </button>
          <Trophy weight="duotone" className="size-5 text-amber-400" />
          <h1 className="text-sm font-extrabold tracking-tight">New experiment</h1>
        </header>

        <main className="min-w-0 flex-1 space-y-6 px-4 py-5 md:px-6">
          {/* Basics */}
          <Section title="Basics">
            <Field label="Name">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Avatar provider bake-off" />
            </Field>
            <Field label="Description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="What are we comparing and why?"
              />
            </Field>
          </Section>

          {/* Modality */}
          <Section title="Modality">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {ARENA_MODALITIES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => changeModality(m)}
                  className={cn(
                    'rounded-xl border p-3 text-left transition-colors',
                    modality === m
                      ? 'border-violet-500/60 bg-violet-500/10'
                      : 'border-border/60 hover:border-border',
                  )}
                >
                  <p className="text-xs font-bold">{MODALITY_META[m].label}</p>
                  <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">{MODALITY_META[m].blurb}</p>
                </button>
              ))}
            </div>
          </Section>

          {/* Task spec */}
          <Section title="Task spec">
            <Field label="Prompt / brief">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                placeholder="The shared prompt sent to every variant (a variant can override it below)."
              />
            </Field>
            {isAvatar && (
              <>
                <Field label="Source image URL">
                  <Input
                    value={sourceImageUrl}
                    onChange={(e) => setSourceImageUrl(e.target.value)}
                    placeholder="https://…/face.png"
                  />
                </Field>
                <Field label="Audio URL">
                  <Input
                    value={audioUrl}
                    onChange={(e) => setAudioUrl(e.target.value)}
                    placeholder="https://…/voice.wav"
                  />
                </Field>
              </>
            )}
          </Section>

          {/* Variants */}
          <Section
            title="Variants"
            action={
              <Button
                size="sm"
                variant="outline"
                className="h-7 rounded-full text-xs"
                onClick={() => setVariants((prev) => [...prev, newVariant(modality)])}
              >
                <Plus weight="bold" className="mr-1 size-3" /> Add
              </Button>
            }
          >
            <div className="space-y-3">
              {variants.map((v, i) => (
                <div key={v.key} className="rounded-xl border border-border/60 bg-background/40 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      Variant {String.fromCharCode(65 + i)}
                    </span>
                    {variants.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setVariants((prev) => prev.filter((x) => x.key !== v.key))}
                        className="text-muted-foreground hover:text-red-400"
                        aria-label="Remove variant"
                      >
                        <Trash weight="bold" className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <Input
                      value={v.label}
                      onChange={(e) => setVariant(v.key, { label: e.target.value })}
                      placeholder={`Label (default ${String.fromCharCode(65 + i)})`}
                      className="text-xs"
                    />
                    <select
                      value={v.generatorKind}
                      onChange={(e) => setVariant(v.key, { generatorKind: e.target.value as ArenaGeneratorKind })}
                      className="h-9 rounded-md border border-input bg-transparent px-2 text-xs"
                    >
                      {ARENA_GENERATOR_KINDS.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                    <Input
                      value={v.model}
                      onChange={(e) => setVariant(v.key, { model: e.target.value })}
                      placeholder="model / slug"
                      className="text-xs"
                    />
                  </div>
                  <Textarea
                    value={v.promptTemplate}
                    onChange={(e) => setVariant(v.key, { promptTemplate: e.target.value })}
                    rows={1}
                    placeholder="Prompt override (optional)"
                    className="mt-2 text-xs"
                  />
                  <Textarea
                    value={v.configOverridesText}
                    onChange={(e) => setVariant(v.key, { configOverridesText: e.target.value })}
                    rows={2}
                    placeholder='Config overrides JSON, e.g. {"size":"1024x1024"}'
                    className={cn('mt-2 font-mono text-[11px]', configErrors[v.key] && 'border-red-500/60')}
                  />
                  {configErrors[v.key] && (
                    <p className="mt-1 text-[10px] text-red-400">{configErrors[v.key]}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* Run settings */}
          <Section title="Run settings">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Samples per variant">
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={sampleCount}
                  onChange={(e) => setSampleCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                />
              </Field>
              <Field label="Budget cap (credits)">
                <Input
                  type="number"
                  min={1}
                  value={budgetCap}
                  onChange={(e) => setBudgetCap(Math.max(1, Number(e.target.value) || 1))}
                />
              </Field>
            </div>
            <Field label="Seed policy">
              <div className="flex items-center gap-2">
                {(['random', 'fixed'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSeedPolicy(p)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium capitalize',
                      seedPolicy === p
                        ? 'border-violet-500/60 bg-violet-500/10 text-violet-300'
                        : 'border-border/60 text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {p}
                  </button>
                ))}
                {seedPolicy === 'fixed' && (
                  <Input
                    type="number"
                    value={fixedSeed}
                    onChange={(e) => setFixedSeed(Number(e.target.value) || 0)}
                    className="ml-2 h-8 w-28 text-xs"
                    placeholder="seed"
                  />
                )}
              </div>
            </Field>
          </Section>
        </main>

        <footer className="sticky bottom-0 z-30 flex items-center justify-end gap-2 border-t border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <Button variant="ghost" onClick={() => router.push('/arena')}>
            Cancel
          </Button>
          <Button
            className="rounded-full bg-violet-500 px-5 font-semibold text-white hover:bg-violet-400"
            disabled={submitting || !name.trim()}
            onClick={() => void submit()}
          >
            {submitting ? 'Creating…' : 'Create experiment'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-bold uppercase tracking-[1.5px] text-muted-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-semibold text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
